import type { SearchSection } from '@setplex/tria-api'
import type { HybridSearchPayloadWithPrice } from '~/shared/interfaces'

import { MediaTypes } from '@setplex/tria-api'

export interface HybridSearchSection {
  key: SearchSection
  /** i18n key for the section header (CR-409 LK-1…LK-4) */
  titleKey: string
  items: HybridSearchPayloadWithPrice[]
}

/** CR-409 LK-1…LK-4; external content reuses the existing MOTN title */
const SECTION_TITLE_KEY: Record<SearchSection, string> = {
  LIVE_EVENTS: 'search_events_results_title',
  WATCH_NOW: 'search_watch_now_results_title',
  CATCHUPS: 'search_catchups_results_title',
  AVAILABLE: 'search_other_results_title',
  EXTERNAL: 'search_external_results_title',
}

/**
 * Maximum number of results a section renders (UI-4.1.3, FR-1.5). Only the
 * sections whose ticket specifies the cap are limited; `AVAILABLE` and
 * `EXTERNAL` keep rendering the full backend list.
 */
export const SECTION_ITEM_LIMIT: Partial<Record<SearchSection, number>> = {
  LIVE_EVENTS: 12,
  WATCH_NOW: 12,
}

/**
 * Fallback grouping used until the AI Search service sends `section` per result
 * (FR-2.1). Catchups have no `contentType` of their own, so they only ever
 * appear once the backend labels them.
 */
const FALLBACK_SECTION_BY_CONTENT_TYPE: Partial<Record<string, SearchSection>> =
  {
    [MediaTypes.LIVE_EVENT]: 'LIVE_EVENTS',
    [MediaTypes.TV_CHANNEL]: 'WATCH_NOW',
    [MediaTypes.TV_SHOW]: 'AVAILABLE',
    [MediaTypes.VOD]: 'AVAILABLE',
  }

export function resolveSearchSection(item: {
  section?: SearchSection
  contentType?: string
}): SearchSection | undefined {
  return (
    item.section ?? FALLBACK_SECTION_BY_CONTENT_TYPE[item.contentType ?? '']
  )
}

/**
 * Groups a flat hybrid search response into sections (BR-8: never one globally
 * merged list).
 *
 * Sections come out in the order the backend first mentions them (FR-3.1), and
 * a section with no results is never created at all, so empty sections render
 * neither a header nor a grid (UI-4.x.1). Items keep the backend order, and a
 * section stops accepting them once it reaches its `SECTION_ITEM_LIMIT`.
 */
export function groupHybridResultsIntoSections(
  items: HybridSearchPayloadWithPrice[]
): HybridSearchSection[] {
  const sections = new Map<SearchSection, HybridSearchSection>()

  for (const item of items) {
    const key = resolveSearchSection(item)
    if (key == null) continue

    let section = sections.get(key)
    if (section == null) {
      section = { key, titleKey: SECTION_TITLE_KEY[key], items: [] }
      sections.set(key, section)
    }

    const limit = SECTION_ITEM_LIMIT[key]
    if (limit != null && section.items.length >= limit) continue

    section.items.push(item)
  }

  return [...sections.values()]
}
